import React, { useState } from 'react';
import { createMemoStyles, defaultFonts, uniqueId, safeRest } from '@wds/utils';
import { IconInfoFilled, IconWarningFilled, IconError, IconCheck, IconLock } from '@wds/icons';
import { CloseButton } from '@wds/internal-components';
import { Slide } from '@wds/motion';
import { getColor } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }
    return t;
  };
  return _assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
var useStyles = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing,
    type = theme.type,
    radius = theme.radius;
  var banner = {
    borderRadius: radius.small,
    bgColor: palette.background.light,
    typeColor: palette.text.dark,
    markWidth: 8,
    borderSize: 1
  };
  return {
    wrenchBanner: function wrenchBanner(_a) {
      var color = _a.color;
      return _assign(_assign({}, defaultFonts(theme)), {
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        fontWeight: type.fontWeight.regular,
        color: banner.typeColor,
        border: [banner.borderSize, 'solid', getColor(palette, color)],
        borderLeftWidth: banner.markWidth,
        borderRadius: banner.borderRadius,
        background: banner.bgColor,
        margin: '0',
        padding: [spacing[5], spacing[6], spacing[5], spacing[5]]
      });
    },
    wrenchBannerBody: function wrenchBannerBody(_a) {
      var showIcon = _a.showIcon;
      return {
        flexGrow: 1,
        paddingLeft: showIcon ? spacing[5] : 0
      };
    },
    wrenchBannerActions: function wrenchBannerActions() {
      return {
        padding: [spacing[4], 0, 0]
      };
    },
    wrenchBannerIcon: function wrenchBannerIcon() {
      return {
        display: 'flex',
        marginTop: 2
      };
    }
  };
});
Object.freeze(['success', 'info', 'upgrade', 'warning', 'error']);
var Actions = function Actions(_a) {
  var wrenchBannerActions = _a.wrenchBannerActions,
    actions = _a.actions;
  return React.createElement("div", {
    className: wrenchBannerActions || ''
  }, actions);
};
var Banner = function Banner(_a) {
  var children = _a.children,
    _b = _a.color,
    color = _b === void 0 ? 'info' : _b,
    _c = _a.showIcon,
    showIcon = _c === void 0 ? false : _c,
    _d = _a.dismissible,
    dismissible = _d === void 0 ? false : _d,
    _e = _a.show,
    show = _e === void 0 ? true : _e,
    onClose = _a.onClose,
    _f = _a.closeButtonLabel,
    closeButtonLabel = _f === void 0 ? 'Close Banner' : _f,
    actions = _a.actions,
    rest = __rest(_a, ["children", "color", "showIcon", "dismissible", "show", "onClose", "closeButtonLabel", "actions"]);
  var _g = useState(false),
    close = _g[0],
    setClose = _g[1];
  var role = color === 'error' || color === 'warning' ? 'alert' : 'status';
  var _h = useStyles({
      color: color,
      showIcon: showIcon
    }),
    wrenchBannerActions = _h.wrenchBannerActions,
    wrenchBanner = _h.wrenchBanner,
    wrenchBannerBody = _h.wrenchBannerBody,
    wrenchBannerIcon = _h.wrenchBannerIcon;
  var bodyId = uniqueId('banner');
  var BannerIcon = {
    info: IconInfoFilled,
    warning: IconWarningFilled,
    error: IconError,
    success: IconCheck,
    upgrade: IconLock
  }[color];
  var handleClick = function handleClick() {
    setClose(true);
    if (onClose) {
      onClose();
    }
  };
  return React.createElement(Slide, {
    in: !close && show,
    direction: "down",
    distance: "far"
  }, React.createElement("div", null, React.createElement("div", _assign({
    className: wrenchBanner,
    role: role,
    tabIndex: 0,
    "aria-describedby": bodyId
  }, safeRest(rest)), dismissible && React.createElement(CloseButton, {
    onClick: handleClick,
    component: "banner",
    label: closeButtonLabel,
    variant: "dark"
  }), showIcon && React.createElement("div", {
    className: wrenchBannerIcon
  }, React.createElement(BannerIcon, {
    color: color,
    title: "".concat(color, " icon.")
  })), React.createElement("div", {
    className: wrenchBannerBody,
    id: bodyId
  }, children, actions && React.createElement(Actions, {
    wrenchBannerActions: wrenchBannerActions,
    actions: actions
  })))));
};
var BANNER_COLORS = ['success', 'warning', 'error', 'info', 'upgrade'];
export { BANNER_COLORS, Banner };